import React from "react";
import {Tooltip} from "antd";
import {
  StyledCardRadioGroup,
  StyledCardRadioOption,
  StyledCardRadioContent,
  StyledCardRadioTitle,
  StyledCardRadioDescription,
  StyledRadioIndicator,
} from "./card-radio-group.style";

export interface CardRadioOption {
  value: string;
  title: string;
  description: string;
  /** Renders the card dimmed and un-selectable. */
  disabled?: boolean;
  /** Explains why the option is unavailable; wraps the card in a tooltip when set. */
  tooltipTitle?: string | null;
}

interface CardRadioGroupProps {
  options: CardRadioOption[];
  value: string;
  onChange: (value: string) => void;
  id?: string;
  name?: string;
  onBlur?: () => void;
  /** Stack cards vertically where subscription tier forms use mobile-touch layout. */
  stackOnMobile?: boolean;
  /**
   * Fill each row with two cards instead of spreading them all across one. A third
   * option starts a second row at the same half width, rather than squeezing every card
   * into a third of the row.
   */
  twoPerRow?: boolean;
}

export const CardRadioGroup = React.forwardRef<HTMLDivElement, CardRadioGroupProps>(
  function CardRadioGroup(
    {
      options,
      value,
      onChange,
      id,
      name,
      onBlur,
      stackOnMobile = false,
      twoPerRow = false,
    },
    ref
  ) {
    const handleOptionClick = (option: CardRadioOption) => {
      if (!option.disabled) {
        onChange(option.value);
      }
    };

    return (
      <StyledCardRadioGroup
        ref={ref}
        id={id}
        $stackOnMobile={stackOnMobile}
        $twoPerRow={twoPerRow}
      >
        {options.map((option) => {
          const selected = value === option.value;

          const card = (
            <StyledCardRadioOption
              key={option.value}
              $selected={selected}
              $disabled={option.disabled}
              onClick={() => handleOptionClick(option)}
            >
              <input
                type="radio"
                id={id ? `${id}-${option.value}` : undefined}
                name={name}
                value={option.value}
                checked={selected}
                onChange={() => handleOptionClick(option)}
                onBlur={onBlur}
                disabled={option.disabled}
              />
              <StyledCardRadioContent>
                <StyledCardRadioTitle>
                  <StyledRadioIndicator $selected={selected} />
                  {option.title}
                </StyledCardRadioTitle>
                <StyledCardRadioDescription>
                  {option.description}
                </StyledCardRadioDescription>
              </StyledCardRadioContent>
            </StyledCardRadioOption>
          );

          return option.tooltipTitle ? (
            <Tooltip title={option.tooltipTitle} key={option.value}>
              <div style={{display: "flex", flex: 1}}>{card}</div>
            </Tooltip>
          ) : (
            card
          );
        })}
      </StyledCardRadioGroup>
    );
  }
);
